import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Save, Scroll } from "lucide-react";
import RichTextEditor from "../components/RichTextEditor";
import SafeHtml from "../components/SafeHtml";

export default function AdminGuidelines() {
  const [content, setContent] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/guidelines").then(({ data }) => {
      const raw = data.content || "";
      // Migrate plain-text content (newlines + bullets) to minimal HTML on first load
      const html = /<[a-z][\s\S]*>/i.test(raw)
        ? raw
        : raw
          .split(/\n\s*\n/)
          .map((para) => {
            const lines = para.split("\n").map((l) => l.trim()).filter(Boolean);
            const allBullets = lines.length > 0 && lines.every((l) => /^[•\-*]\s+/.test(l));
            if (allBullets) {
              return `<ul>${lines.map((l) => `<li>${l.replace(/^[•\-*]\s+/, "")}</li>`).join("")}</ul>`;
            }
            return `<p>${lines.join("<br/>")}</p>`;
          })
          .join("");
      setContent(html);
      setUpdatedAt(data.updated_at);
      setLoaded(true);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/guidelines", { content });
      setUpdatedAt(data.updated_at);
      toast.success("Guidelines saved. Clients will see the update on refresh.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-guidelines-page">
      <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))]">Client portal</div>
      <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2 flex items-center gap-3">
        <Scroll className="w-8 h-8 text-[hsl(var(--brand-sage))]" /> Guidelines
      </h1>
      <p className="text-[hsl(var(--brand-muted))] mt-2 max-w-2xl">
        Shown on every client's home page below the recipe book. Use this for water intake, exercise, sleep — anything you'd normally repeat on WhatsApp.
      </p>

      <div className="mt-8">
        {loaded ? (
          <RichTextEditor value={content} onChange={setContent} placeholder="Use the toolbar for headings, bold, lists" />
        ) : (
          <div className="border border-[hsl(var(--brand-border))] bg-white rounded-md px-4 py-8 font-serif-italic text-[hsl(var(--brand-muted))]">Loading…</div>
        )}
        <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs text-[hsl(var(--brand-muted))]">
            {updatedAt ? `Last updated ${new Date(updatedAt).toLocaleString()}` : "Not saved yet"}
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))] disabled:opacity-60"
            data-testid="save-guidelines-btn"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save guidelines"}
          </button>
        </div>
      </div>

      <div className="mt-10">
        <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-muted))]">Preview</div>
        <div className="mt-3 bg-[hsl(var(--brand-cream-deep))] border border-[hsl(var(--brand-border))] rounded-xl p-8">
          <div className="font-display text-3xl">A few gentle guidelines</div>
          <SafeHtml html={content} className="nourish-prose mt-4 leading-relaxed text-[hsl(var(--brand-ink))]/90" />
        </div>
      </div>
    </div>
  );
}
